/**
 * Multi-select selection cap (spec 36, feature 2).
 *
 * `max_selections` has existed as data for a long time — the AI parse prompt asks for it, it is
 * persisted on `survey.questions`, and it ships to the browser in the public payload — but nothing
 * read it. Everything decision-shaped about honouring it lives here: pure, no DOM, no network, so
 * the rules are testable on their own and SurveySession only carries the wiring.
 *
 * Two rules do the real work:
 *   * The cap is a CEILING, never a quota. Submitting fewer is always allowed; forcing a count
 *     makes people pad answers they do not mean.
 *   * At the cap only UNSELECTED options lock. A respondent who picked their limit and changed
 *     their mind has to be able to swap, so a SELECTED option always stays clickable. (This is
 *     where the `ranking_count` pattern diverges: ranking HIDES chosen items, which is right for
 *     ranking and wrong here.)
 */

/** The one question type whose cap the runtime honours. On any other type it is ignored. */
export const MULTI_SELECT_TYPE = 'multiple_choice';

/**
 * Legacy choice-level media. The respondent picks IMAGE TILES — one per `media_urls` entry — and
 * answers `{ selected_indices: [...] }`; `choices` only labels the tiles and `SurveySession` falls
 * back to "Image N" wherever a label is missing.
 *
 * The renderer does NOT test for this value. It computes `media_display_mode || 'question'` and
 * takes the tile branch on anything `!== 'question'`, so a stored "choice", "option" or any other
 * non-empty value is choice-mode to the respondent. Matching the constant exactly, as both
 * validators used to, is the same two-sides-disagree class one level up from the count itself:
 * the browser would tile six images while the server counted two labels.
 */
export const MEDIA_QUESTION_DISPLAY_MODE = 'question';

/**
 * The server's refusal of an over-cap answer, as the two things the browser may key on.
 *
 * They live HERE, beside the rule they belong to, rather than in `SurveySession` — the respondent
 * gate is the only place either is read, but the shared contract
 * (`src/__tests__/fixtures/selectionCapContract.json`) is what keeps them true, and the parity
 * suite must be able to drive them without mounting a survey component.
 *
 * `SELECTION_CAP_REJECTION_CODE` is the machine-readable reason the server tags this one rejection
 * with; it is the ONLY thing that should decide, and the code is authoritative wherever present.
 * `SELECTION_CAP_MESSAGE` recognises the untagged wording, and exists only because the two repos
 * deploy separately: a browser running against a server that predates the code still has to tell
 * the respondent what happened rather than fall back to "please try again". Both are pinned in the
 * shared contract's `submit_rejection` section, so a server-side reword or rename turns this
 * suite red instead of silently reverting the one rejection this feature exists to explain.
 */
export const SELECTION_CAP_REJECTION_CODE = 'max_selections_exceeded';
export const SELECTION_CAP_MESSAGE = /^select at most \d+ option\(s\); got \d+\.$/i;

/** Implicit options for a yes_no question, which stores no `choices` array of its own. */
const YES_NO_TYPE = 'yes_no';
const YES_NO_CHOICES = ['Yes', 'No'];

/** Keys a dict-shaped choice may carry its comparable value under, in the order the server reads. */
const CHOICE_VALUE_KEYS = ['value', 'label', 'text'] as const;

const COUNTER_LABEL = 'Selected';
const HINT_TEMPLATE = (max: number) => `(select up to ${max})`;

/**
 * Numbers an author may have spelled out instead of writing a digit. Without these, "select up to
 * three" did not match the constraint pattern at all and the runtime hint doubled up on it.
 * Digits cover everything above this range; a cap in words beyond ten is not a real survey.
 */
const NUMBER_WORDS: Record<string, number> = {
  one: 1, two: 2, three: 3, four: 4, five: 5,
  six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
};

/**
 * Digits, and whitespace, spelled out as explicit classes rather than written `\d` and `\s`.
 *
 * Both shorthands mean DIFFERENT things in the two engines, and this pattern is mirrored
 * character-for-character into the backend's `survey_validation_service.py`:
 *
 *   * Python's `\d` matches every Unicode decimal digit while JavaScript's is ASCII-only, so
 *     "Select up to \u0663 options" read a cap of 3 on the server and nothing here — the server
 *     warning an author about a limit the browser will never show or enforce. Six digit scripts
 *     were measured diverging. ASCII-only is the direction that makes the two agree on NOTHING
 *     STATED, which is the safe one: a cap the respondent's UI cannot honour is not a cap.
 *   * The two `\s` sets differ at three codepoints, in BOTH directions: JavaScript matches U+FEFF
 *     and Python does not, Python matches U+0085 and U+001C-U+001F and JavaScript does not. The
 *     class below is exactly their intersection, so an NBSP or an ideographic space pasted out of
 *     a document still reads on both sides while nothing reads on only one.
 *
 * Measured, not reasoned: `work-dirs/spec36-cap-round7/nonascii_divergence.py` injects 58
 * non-ASCII codepoints into real cap wordings and runs BOTH implementations. It found 21
 * divergences and now finds none. The live-corpus differential cannot see any of this, because no
 * live question text contains a non-ASCII digit — which is exactly why it needs its own check.
 */
const DIGIT = '[0-9]';
const SPACE =
  '[ \\t\\n\\r\\f\\v\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000]';

/**
 * A written number, digit groups included. `\d+` alone stopped at the separator and read "1,000"
 * as 1 — reported to the author as "your wording states a limit of 1", which is both wrong and
 * the most confusing possible way to be wrong.
 *
 * The grouped form is written first for readability ONLY. Measured, not assumed: swapping the two
 * alternatives changes no read at all (0 of 6,897 — every live PROD+DEV question text, every
 * fixture case and sixteen separator shapes), because `COUNT_END`'s digit guard rejects the
 * truncated `\d+` match and the engine then backtracks into the grouped alternative by itself.
 * The GUARD is what makes this work; do not let this ordering read as the thing holding it up.
 *
 * A malformed group ("1,00") matches neither alternative past the "1", and that same guard then
 * rejects it outright rather than reading the truncation.
 */
const DIGITS = `${DIGIT}{1,3}(?:,${DIGIT}{3})+|${DIGIT}+`;
/** Stripped before the value is read, so `parseInt`/`int` never see the separator. */
const DIGIT_GROUP_SEPARATOR = /,/g;

const NUMBER = `(?:${DIGITS}|${Object.keys(NUMBER_WORDS).join('|')})`;
/**
 * "one" is excluded after `which`: "tell us which one mattered most" is a pronoun, not a count, and
 * it read as a stated cap of 1 on four live PROD questions.
 */
const NUMBER_NOT_ONE =
  `(?:${DIGITS}|${Object.keys(NUMBER_WORDS).filter((w) => w !== 'one').join('|')})`;
const SELECT_VERB = '(?:select|choose|pick|tick|check|mark)';
const QUANTIFIER =
  `(?:up${SPACE}+to|at${SPACE}+most|no${SPACE}+more${SPACE}+than|a${SPACE}+maximum${SPACE}+of`
  + `|maximum${SPACE}+of|max${SPACE}+of|exactly|only)`;
/**
 * `the` is an ARTICLE, not a quantifier, and it used to sit in the list above with nothing
 * guarding what followed it. It reads a real limit on live PROD wordings ("Pick the 3 that fit
 * best"), so it keeps its own alternative — but only under the two guards below.
 */
const ARTICLE = 'the';
/** "1-3", "2 to 4", "2–3": the ceiling is the UPPER bound. Reading the lower one says 1. */
const RANGE = `${SPACE}*(?:-|\u2013|\u2014|to)${SPACE}*`;

/**
 * Spelled out rather than written `\b`. This pattern is mirrored character-for-character in the
 * backend's `survey_validation_service.py`, where `\b` is UNICODE-aware while JavaScript's is
 * ASCII-only — so on a word starting after a non-ASCII letter the two engines would quietly
 * disagree and the mirror would stop being one. Both spell the SAME class, so both are ASCII-only.
 *
 * `WORD_START` is the ONE place the two sides differ in syntax, and it has to. The backend keeps
 * the lookbehind `(?<![A-Za-z0-9_])`; this side cannot, because lookbehind is unsupported before
 * Safari 16.4 and the repo's build target still reaches `safari14` (Vite's default `'modules'`
 * list). This is a runtime string compiled at module scope, and a target list cannot rewrite one —
 * so on an older iPhone `new RegExp` threw a `SyntaxError` while this module was being evaluated,
 * which took `SurveySession.tsx` down with it and left the respondent a blank survey route for
 * EVERY question, capped or not. `src/__tests__/regexBrowserSupport.test.ts` is the standing guard;
 * the esbuild measurements behind it are in that file's docblock.
 *
 * `(?:^|[^A-Za-z0-9_])` CONSUMES the character the lookbehind only looked at, which is why nothing
 * else needed adjusting: the group is non-capturing, so every capture index is where it was, and
 * `statedSelectionCap` reads groups only — never `match.index` or `match[0]` — so a match that now
 * starts one character earlier is not observable. The alternation order is not observable either:
 * at input start the engine tries `^` (matching from 0) before the consuming branch (matching from
 * 1), so the two forms could only pick different alternatives if `text[0]` were both a word
 * character (to start a verb) and a non-word one (to be consumed).
 *
 * Measured, not argued: `work-dirs/spec36-cap-round8/wordstart_equivalence.mjs` runs both forms
 * over the 543 live PROD+DEV question texts, every fixture row, the non-ASCII harness and 3,000
 * generated prefix/suffix shapes. They agree on every one.
 */
const WORD_START = '(?:^|[^A-Za-z0-9_])';
const WORD_END = '(?![A-Za-z0-9_])';

/**
 * A percentage written out instead of punctuated. `50%` and `50 percent` are the same magnitude,
 * and the guard below excluded only the first spelling: "Select up to 100 percent of the range"
 * read a cap of 100 and "Choose at most 50 percent of these" read 50, on the ordinary quantifier
 * branch, long after the `%` form was closed.
 */
const PERCENT_WORD = `(?:percent(?:age)?|per${SPACE}+cent)`;

/**
 * What a captured number may be followed by. Three things it may not be:
 *
 *   * more of a word — `WORD_END`;
 *   * a percentage in either spelling, because a percentage is a magnitude and never a count of
 *     selections. "Choose the 100% natural option" and "Select up to 100% of the range" both used
 *     to read as caps. Applied on every branch, since none of them is a place a percentage could
 *     mean a number of options;
 *   * a `,` or `.` with a DIGIT behind it, which means the number did not end where the match did.
 *     "Choose at most 3.5 options" stated a limit of 3. A fractional is not a count of selections
 *     at all — `selectionCap` already discards a stored one as unusable authoring — so it states
 *     nothing rather than a rounded something. The digit is what distinguishes a separator from
 *     ordinary punctuation, leaving "Select up to 3, or fewer" and "Pick the 3." alone.
 */
const COUNT_END =
  `(?![A-Za-z0-9_%])(?!${SPACE}*${PERCENT_WORD}${WORD_END})(?![.,]${DIGIT})`;

/**
 * End of input, spelled as "no character follows" rather than `$`. Python's `$` ALSO matches just
 * before a trailing newline while JavaScript's (without `m`) does not — the same class of
 * mirror-breaking difference as `\b`.
 *
 * Measured, not assumed: in THIS pattern the two spellings are indistinguishable (0 of 24,166
 * strings — every live question text and fixture case under ten different endings — separate
 * them), because the `\s*` in front of the alternation backtracks over a trailing newline and
 * reaches true end-of-string anyway. That masking is the reason to spell it out rather than to
 * rely on `$`: it holds only while that `\s*` is there, and nothing would announce its removal.
 */
const INPUT_END = '(?![\\s\\S])';

/**
 * The number after `the` has to be the HEAD of its phrase — the thing being counted — not a
 * modifier of the noun after it. "Pick the 3 that fit best" counts; "Pick the 2024 model" and
 * "Select the 3 star rating" describe. A relative pronoun, "you", an `of`-phrase or the end of the
 * clause is what marks the head reading. Anything else is a MISS, which is the safe direction: the
 * matcher already misses 8 of the 19 capped PROD questions, while a wrong read tells an author
 * their own wording states a limit they never wrote.
 */
const HEAD_MARKERS = `(?:that|which|who|whom|you|of)${WORD_END}|[.,;:!?)]|${INPUT_END}`;
const COUNT_HEAD = `(?=${SPACE}*(?:${HEAD_MARKERS}))`;

/**
 * The same head test for the number after `which`, which needs a LOOSER one than `COUNT_HEAD`.
 *
 * After `the`, the count is normally the whole noun phrase and a relative pronoun or the end of
 * the clause marks it. After `which` it normally HEADS one — "Which TWO flavor directions would
 * make you…" (PROD 191 Q18, cap 2) — which `COUNT_HEAD` rejects out of hand. Applying it verbatim
 * here would drop a live author's number.
 *
 * What separates the two readings is agreement: a cardinal of two or more forces a PLURAL head
 * ("which two flavor directions"), while a year or a magnitude modifies a singular one ("which
 * 2024 model", "which 100 percent natural option"). So the count is read when it is followed by
 *
 *   * the `COUNT_HEAD` markers, as on the article branch — "Which 3 of these", "Which 3.";
 *   * an auxiliary, which is what the interrogative puts between a bare numeral and the rest when
 *     the numeral IS the whole noun phrase — "Which 3 do you prefer?";
 *   * a plural noun, optionally behind one modifier, which is the counted thing itself.
 *
 * Anything else is a MISS, the safe direction: over the 6,796 live PROD+DEV question texts this
 * changes not one read, while a wrong read tells an author their own wording states a limit of
 * 2024. The one shape it still cannot see is a year RANGE over a plural noun ("Which 2024-2025
 * models…"), where agreement says nothing — filed rather than guessed at.
 */
const AUXILIARY =
  '(?:do|does|did|is|are|was|were|will|would|can|could|shall|should|may|might|must'
  + '|have|has|had)';
const NOUN_WORD = "[A-Za-z][A-Za-z'’-]*";
const WHICH_COUNT_HEAD =
  `(?=${SPACE}*(?:${HEAD_MARKERS})`
  + `|${SPACE}+${AUXILIARY}${WORD_END}`
  + `|${SPACE}+(?:${NOUN_WORD}${SPACE}+)?${NOUN_WORD}s${WORD_END})`;

/**
 * Matches an author who already wrote the constraint into the question text, in any casing.
 *
 * Five alternatives, each capturing the number that is the CEILING. Measured against all 367
 * `multiple_choice` questions on PROD + DEV rather than invented, because a matcher for human
 * phrasing is only as good as the phrasings it was checked on. On the 19 capped PROD questions it
 * reads the author's number on 11 and reads NONE of the other 8 wrong (the literal "select up to
 * N" read 2 of the 19); on the 223 uncapped PROD ones it fires once, correctly ("Pick the 3 that
 * matter most to you"), where nothing consults it anyway. The exact cases, with the surveys they
 * came from, are the shared contract `src/__tests__/fixtures/selectionCapContract.json`.
 *
 * A QUANTIFIER is required outside the range alternative. Allowing a bare verb + number matched
 * "more likely to pick one candy over another" and called it a cap of 1.
 *
 * The ARTICLE alternative is separate from the quantifier one, and tighter, because `the` does not
 * quantify anything on its own: it needs `NUMBER_NOT_ONE` (the pronoun in "Pick the one that best
 * describes you" — 74 live PROD+DEV texts — is not a cap of 1, exactly as after `which`) and
 * `COUNT_HEAD` (the number must be what is counted, not a modifier of the next noun).
 *
 * BOTH `which` alternatives carry the same requirement, under `WHICH_COUNT_HEAD` — the wider head
 * test the interrogative needs. They went without one for a round while the article branch had it,
 * which is the whole reason "Which 2024 model do you own?" read a cap of 2024.
 */
const HINT_PATTERN = new RegExp(
  `${WORD_START}${SELECT_VERB}${SPACE}+(?:${QUANTIFIER}${SPACE}+)?${NUMBER}${RANGE}`
  + `(${NUMBER})${COUNT_END}`
  + `|${WORD_START}${SELECT_VERB}${SPACE}+${QUANTIFIER}${SPACE}+(${NUMBER})${COUNT_END}`
  + `|${WORD_START}${SELECT_VERB}${SPACE}+${ARTICLE}${SPACE}+(${NUMBER_NOT_ONE})`
  + `${COUNT_END}${COUNT_HEAD}`
  + `|${WORD_START}which${SPACE}+${NUMBER_NOT_ONE}${RANGE}(${NUMBER})${COUNT_END}`
  + `${WHICH_COUNT_HEAD}`
  + `|${WORD_START}which${SPACE}+(${NUMBER_NOT_ONE})${COUNT_END}${WHICH_COUNT_HEAD}`,
  'i',
);

/** An entry in a multi-select answer: a plain choice string, or the "Other" object. */
export type MultiSelectItem = string | { choice: string; other_text?: string };

/**
 * Which of the two entry SHAPES a call is about.
 *
 * A multi-select answer holds two kinds of entry and they are not interchangeable: an ordinary
 * option is the bare choice string, while "Other" is stored as `{ choice: 'Other', other_text }`
 * so the typed text has somewhere to live. Keying identity on the choice VALUE alone collided the
 * two — on a question whose options literally include "Other", clicking the Other box removed that
 * option instead of opening the text input. Identity is shape AND value, so they cannot collide.
 */
export const SELECTION_SHAPE = { PLAIN: 'plain', OTHER: 'other' } as const;
export type SelectionShape = (typeof SELECTION_SHAPE)[keyof typeof SELECTION_SHAPE];

interface CappedQuestion {
  type?: string;
  max_selections?: number | null;
}

/** Everything the option count reads. Deliberately loose — stored questions predate the schema. */
interface CountableQuestion {
  type?: string;
  choices?: unknown;
  allow_other?: boolean | null;
  media_urls?: unknown;
  media_display_mode?: string | null;
  option_media?: Record<string, unknown> | null;
}

/**
 * A single choice entry as its comparable string, or undefined for an unusable one.
 *
 * Mirrors the server's `_choice_to_str`: choices are normally plain strings, but dict entries
 * carrying `value` / `label` / `text` are tolerated in the same key order.
 */
function choiceToValue(choice: unknown): string | undefined {
  if (typeof choice === 'string') return choice;
  if (choice && typeof choice === 'object') {
    for (const key of CHOICE_VALUE_KEYS) {
      const value = (choice as Record<string, unknown>)[key];
      if (typeof value === 'string') return value;
    }
  }
  return undefined;
}

/** A question's exact choice values, including the implicit Yes/No a yes_no question stores none of. */
function questionChoiceValues(question: CountableQuestion | null | undefined): string[] {
  const raw = question?.choices;
  const values = Array.isArray(raw)
    ? raw.map(choiceToValue).filter((value): value is string => value !== undefined)
    : [];
  if (values.length === 0 && question?.type === YES_NO_TYPE) return [...YES_NO_CHOICES];
  return values;
}

/**
 * Whether the runtime draws one selectable IMAGE TILE per `media_urls` entry for this question.
 *
 * Three conditions, all read off the renderer (`SurveySession.tsx`:
 * `hasMediaMultiple && !isQuestionModeMultiple && !hasNewOptionMediaMultiple`, where
 * `isQuestionModeMultiple` is `(media_display_mode || 'question') === 'question'`):
 *
 *   * there is at least one media url to tile;
 *   * the display mode is stated and is not "question" — NOT "is exactly 'choices'", which is
 *     narrower than the renderer and would disagree with it on any other stored value;
 *   * `option_media` is absent — the one that is easy to miss. Per-option media sends the question
 *     BACK to its ordinary choice list even in the legacy mode, so the tiles are not what the
 *     respondent picks from, and the "Other" that list DOES draw counts again.
 *
 * The mode is tested as a non-empty string rather than for JavaScript truthiness, so the two
 * languages agree on every JSON value: `[]` is truthy here and falsy in Python, and the field is
 * only ever a string.
 *
 * EXPORTED because `SurveySession`'s own `canProceed` has to ask the same question and used to ask
 * a narrower one (`media_display_mode === 'choices'`). That left the two halves of this feature
 * encoding different rules: on a stored `media_display_mode: 'option'` the respondent got image
 * tiles and a `{selected_indices}` answer while `canProceed` took the choice-ARRAY branch, so
 * `Array.isArray` was false and Next never enabled — a question nobody could get past, with the
 * cap bound to a branch that shape cannot reach. Same for `'choices'` alongside `option_media`,
 * where the renderer goes BACK to the choice list and the strict test still demanded indices.
 */
export function rendersImageTiles(question: CountableQuestion | null | undefined): boolean {
  const mode = question?.media_display_mode;
  const mediaUrls = question?.media_urls;
  const optionMedia = question?.option_media;
  return typeof mode === 'string' && mode !== '' && mode !== MEDIA_QUESTION_DISPLAY_MODE
    && Array.isArray(mediaUrls) && mediaUrls.length > 0
    && !(optionMedia && Object.keys(optionMedia).length > 0);
}

/**
 * How many options a respondent can actually pick from — what a cap has to fit inside.
 *
 * MIRRORS the server's `_selectable_option_count`, which is the number the publish validator
 * rejects `max_selections_exceeds_choices` against. Counting a different list in the browser is
 * not a cosmetic difference: it clamps the author's typed cap to the wrong ceiling, and it accepts
 * caps the server then refuses at activation with no control able to clear them.
 *
 * Normally the count is `choices`, plus one for "Other" when the author enabled it, since "Other"
 * is a real extra thing to pick. A legacy choice-level-media question is the exception: it offers
 * one tile per `media_urls` entry, `choices` merely labels those tiles and may be short or absent,
 * and that renderer draws no "Other" affordance at all — so a stale `allow_other` (whose toggle
 * hides itself once the question has media) must not raise the ceiling.
 *
 * "Other" is only ever added to a non-empty list: a question with no choices offers nothing, which
 * `empty_choices` already reports, and inventing an Other-only option there would let a cap through
 * on a question nobody can answer.
 */
export function selectableOptionCount(question: CountableQuestion | null | undefined): number {
  if (rendersImageTiles(question)) return (question!.media_urls as unknown[]).length;
  const count = questionChoiceValues(question).length;
  return count && question?.allow_other ? count + 1 : count;
}

/** The choice value an answer entry stands for; undefined for an unusable entry. */
export function selectionValue(item: MultiSelectItem): string | undefined {
  if (typeof item === 'string') return item;
  return item && typeof item === 'object' ? item.choice : undefined;
}

/** Which shape an existing entry is stored in; undefined for an unusable entry. */
function shapeOf(item: MultiSelectItem): SelectionShape | undefined {
  if (typeof item === 'string') return SELECTION_SHAPE.PLAIN;
  if (item && typeof item === 'object' && typeof item.choice === 'string') {
    return SELECTION_SHAPE.OTHER;
  }
  return undefined;
}

/** Shape + value, the key everything matches on. Undefined entries can never match a target. */
function identityOf(item: MultiSelectItem): string | undefined {
  const shape = shapeOf(item);
  return shape === undefined ? undefined : `${shape}:${selectionValue(item)}`;
}

const targetIdentity = (choice: string, shape: SelectionShape) => `${shape}:${choice}`;

/** The entry to append for a choice, in the shape the caller asked for. */
function buildSelection(choice: string, shape: SelectionShape): MultiSelectItem {
  return shape === SELECTION_SHAPE.OTHER ? { choice, other_text: '' } : choice;
}

/** An answer as a selection array, tolerating the not-answered-yet and wrong-shape cases. */
export function toSelections(response: unknown): MultiSelectItem[] {
  return Array.isArray(response) ? (response as MultiSelectItem[]) : [];
}

/**
 * The cap in force for a question, or null for "no limit".
 *
 * A 0/negative/fractional value is invalid authoring (the server blocks publishing one); treating
 * it as a limit would lock the respondent out of a question they could never satisfy, which is
 * strictly worse than ignoring it.
 */
export function selectionCap(question: CappedQuestion | null | undefined): number | null {
  if (!question || question.type !== MULTI_SELECT_TYPE) return null;
  const cap = question.max_selections;
  if (typeof cap !== 'number' || !Number.isInteger(cap) || cap <= 0) return null;
  return cap;
}

/**
 * How many options an answer selects.
 *
 * Handles both shapes a multi-select answer takes: the ordinary array of choices, and the legacy
 * image-choice `{ selected_indices: [...] }`. Anything else counts as 0.
 */
export function selectionCount(response: unknown): number {
  if (Array.isArray(response)) return response.length;
  const indices = (response as { selected_indices?: unknown } | null)?.selected_indices;
  return Array.isArray(indices) ? indices.length : 0;
}

export function isChoiceSelected(
  response: unknown,
  choice: string,
  shape: SelectionShape = SELECTION_SHAPE.PLAIN,
): boolean {
  const target = targetIdentity(choice, shape);
  return toSelections(response).some((item) => identityOf(item) === target);
}

/** Whether one MORE selection would breach the cap. */
export function isAddBlocked(count: number, cap: number | null): boolean {
  return cap !== null && count >= cap;
}

/** Whether an option should be unavailable: at the cap, and not already selected. */
export function isChoiceLocked(
  response: unknown,
  choice: string,
  cap: number | null,
  shape: SelectionShape = SELECTION_SHAPE.PLAIN,
): boolean {
  if (isChoiceSelected(response, choice, shape)) return false;
  return isAddBlocked(selectionCount(response), cap);
}

/**
 * The single toggle every multi-select call site uses.
 *
 * Returns the selections unchanged when the cap blocks an add, so callers can assign the result
 * unconditionally. Removals are never blocked — that is what lets a respondent swap at the cap.
 *
 * `shape` says WHICH entry the caller means — the bare choice string, or the "Other" object. It
 * decides both what is matched for removal and what is appended, so the two can never be mixed up.
 */
export function toggleMultiSelect(
  response: unknown,
  choice: string,
  cap: number | null,
  shape: SelectionShape = SELECTION_SHAPE.PLAIN,
): MultiSelectItem[] {
  const selections = toSelections(response);
  const target = targetIdentity(choice, shape);
  if (selections.some((item) => identityOf(item) === target)) {
    return selections.filter((item) => identityOf(item) !== target);
  }
  if (isAddBlocked(selections.length, cap)) return selections;
  return [...selections, buildSelection(choice, shape)];
}

/** Whether an answer satisfies the cap. Fewer than the cap is fine; more is not. */
export function isWithinSelectionCap(response: unknown, cap: number | null): boolean {
  return cap === null || selectionCount(response) <= cap;
}

/** The running count shown beside the options, or null when there is no cap to count towards. */
export function selectionCounterText(response: unknown, cap: number | null): string | null {
  if (cap === null) return null;
  return `${COUNTER_LABEL}: ${selectionCount(response)} of ${cap}`;
}

/**
 * The limit the AUTHOR wrote into the question text, or null if they wrote none.
 *
 * Read rather than merely detected: an author's stale "select up to 5" beside a cap of 3 is a
 * contradiction that has to be resolved in favour of the enforced number, not silently obeyed.
 */
export function statedSelectionCap(text: string | undefined | null): number | null {
  const match = HINT_PATTERN.exec(text ?? '');
  if (!match) return null;
  // Exactly one alternative matched, so exactly one group is defined; which one is not interesting.
  // The separators come out before the value is read: `parseInt('1,000', 10)` is 1 in JavaScript
  // and `int('1,000')` RAISES in Python, so leaving them in is a mirror that breaks two ways.
  const token = match.slice(1).find((group) => group !== undefined)
    ?.toLowerCase().replace(DIGIT_GROUP_SEPARATOR, '');
  if (token === undefined) return null;
  const stated = NUMBER_WORDS[token] ?? Number.parseInt(token, 10);
  return Number.isInteger(stated) ? stated : null;
}

/**
 * The question text the respondent reads, carrying the constraint.
 *
 * Applied at RUNTIME rather than written into the stored text: the cap is editable, and baking the
 * hint into `question.text` would leave a stale "select up to 3" behind the moment an author
 * lowered or cleared it — and would corrupt the text every analysis and export reads.
 *
 * The hint is suppressed only when the author's own words state the SAME number. Suppressing it on
 * any "select up to N" showed the respondent the author's number while the runtime enforced a
 * different one — reinstating the stale hint this design exists to avoid. When the two disagree
 * the enforced number is appended anyway, so it always matches the counter and the locked options;
 * the contradiction itself is surfaced to the author as a publish warning
 * (`max_selections_text_conflict`), which is where it can actually be fixed.
 */
export function questionTextWithCap(text: string | undefined | null, cap: number | null): string {
  const base = text ?? '';
  if (cap === null || !base.trim() || statedSelectionCap(base) === cap) return base;
  return `${base} ${HINT_TEMPLATE(cap)}`;
}
